import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, normalizeUserRoleKey } from './AuthContext';
import { getPartnerOnboardingPath, isPartnerRole, resolveLoginRole, getLoginPathForRoute } from './loginNavigation';

const MAX_LOADING_MS = 8000;

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  // Wait for auth to initialize; cap wait so we never spin forever
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setIsChecking(false), 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // If auth loading takes too long, stop showing spinner so user can at least see login/content
  useEffect(() => {
    const timer = setTimeout(() => setIsChecking(false), MAX_LOADING_MS);
    return () => clearTimeout(timer);
  }, []);

  // Show loading while checking or if auth is loading (but not after max wait)
  if ((loading || isChecking) && isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login, preserving the intended destination so we can return after auth
    return <Navigate to={getLoginPathForRoute(location.pathname)} state={{ from: location }} replace />;
  }

  if (user.onboardingComplete === false && allowedRoles.length > 0) {
    const role = normalizeUserRoleKey(user.role);
    if (isPartnerRole(role)) {
      return <Navigate to={getPartnerOnboardingPath(role)} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const userRoleFromStorage = localStorage.getItem('userRole');
  const storedUserRole = (() => {
    try {
      const raw = localStorage.getItem('user');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.role || null;
    } catch (_) {
      return null;
    }
  })();
  const rawRole =
    user.role ||
    (userRoleFromStorage && userRoleFromStorage !== 'undefined' ? userRoleFromStorage : null) ||
    (storedUserRole && storedUserRole !== 'undefined' ? storedUserRole : null);
  const userRole = resolveLoginRole(rawRole);

  if (allowedRoles.length > 0) {
    const normalizedUserRole = normalizeUserRoleKey(userRole);
    const normalizedAllowedRoles = allowedRoles.map((role) => normalizeUserRoleKey(role));
    const hasAccess = normalizedAllowedRoles.includes(normalizedUserRole);
    
    if (!hasAccess) {
      console.warn('Access denied:', { 
        userRole: user.role, 
        userRoleFromStorage,
        userRoleNormalized: normalizedUserRole,
        allowedRoles, 
        allowedRolesNormalized: normalizedAllowedRoles,
        userObject: user 
      });
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">
                You don't have permission to access this page.
                {userRole && (
                  <span className="block mt-2 text-sm">
                    Your role: <strong>{userRole}</strong>
                  </span>
                )}
              </p>
              <a
                href="/"
                className="inline-block bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition"
              >
                Go to Home
              </a>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
